import React from 'react';
import { motion } from 'framer-motion';
import { FadeIn, StaggerContainer, StaggerItem, RevealText, SlideReveal } from '../components/AnimatedSection';
import { FloatingHexagons, HexShape, HexDivider } from '../components/HexagonLogo';
import { ParallaxImage, RevealImage, WordReveal, MagneticButton } from '../components/ScrollEffects';
import { FaLinkedinIn, FaInstagram } from 'react-icons/fa';

export default function About() {
  return (
    <div className="page-wrapper">
      {/* Hero with background */}
      <section style={styles.heroSection}>
        <div style={styles.heroBg}>
          <motion.img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80"
            alt="Modern office"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
          />
          <div style={styles.heroOverlay} />
        </div>
        <FloatingHexagons />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <FadeIn>
            <div className="section-num">01 &mdash; ABOUT US</div>
          </FadeIn>
          <RevealText>
            <h1 className="section-title" style={{ fontSize: 'clamp(2.8rem, 6vw, 4.5rem)', maxWidth: 700 }}>
              Built by industry<br />insiders. <span className="gradient-text">Powered by AI.</span>
            </h1>
          </RevealText>
          <FadeIn delay={0.3}>
            <p className="section-subtitle" style={{ maxWidth: 560 }}>
              We understand manufacturing because we come from it.
              HTV Digital combines factory-floor expertise with AI-powered
              creativity to help industrial brands dominate digitally.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* HTV Meaning */}
      <section style={{ background: '#111112', padding: '0' }}>
        <div className="grid-mission">
          {htvValues.map((val, i) => (
            <FadeIn key={i} delay={i * 0.15}>
              <motion.div
                style={{ ...styles.htvCard, borderRight: i < 2 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}
                whileHover={{ background: 'rgba(62, 117, 98, 0.04)' }}
              >
                <span style={{ ...styles.htvLetter, color: val.color }}>{val.letter}</span>
                <h3 style={styles.htvName}>{val.name}</h3>
                <div style={{ width: 40, height: 2, background: val.color, margin: '16px 0', opacity: 0.5 }} />
                <p style={styles.htvDesc}>{val.desc}</p>
              </motion.div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* Parallax Image Break */}
      <ParallaxImage
        src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1920&q=80"
        alt="Team collaboration workspace"
        height={400}
        speed={0.3}
      />

      {/* Mission / Vision / Approach with images */}
      <section className="section" style={{ background: '#0B0B0B' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 80 }}>
            <FadeIn>
              <div className="section-num" style={{ justifyContent: 'center' }}>04 &mdash; OUR PHILOSOPHY</div>
            </FadeIn>
            <WordReveal
              text="Mission. Vision. Approach."
              className="section-title"
              style={{ fontSize: 'clamp(2rem, 5vw, 3.2rem)', fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1.15, display: 'block' }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {missionCards.map((card, i) => (
              <SlideReveal key={i} direction={i % 2 === 0 ? 'left' : 'right'} delay={i * 0.1}>
                <motion.div
                  style={{
                    ...styles.missionCard,
                    borderTop: i === 0 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                  }}
                  whileHover={{ background: 'rgba(62, 117, 98, 0.03)' }}
                >
                  <div style={styles.missionLeft}>
                    <span style={styles.missionNum}>0{i + 1}</span>
                    <HexShape size={48} color="#3E7562" opacity={0.15} />
                  </div>
                  <div style={styles.missionCenter}>
                    <h3 style={styles.missionTitle}>{card.title}</h3>
                    <p style={styles.missionDesc}>{card.desc}</p>
                  </div>
                  <div style={{ flexShrink: 0, width: 180, height: 140 }}>
                    <RevealImage
                      src={card.image}
                      alt={card.title}
                      style={{ width: '100%', height: '100%' }}
                    />
                  </div>
                </motion.div>
              </SlideReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Parallax Image Break */}
      <ParallaxImage
        src="https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=1920&q=80"
        alt="Manufacturing facility"
        height={350}
        speed={0.25}
      />

      {/* Opportunity */}
      <section className="section" style={{ background: '#111112' }}>
        <div className="container">
          <FadeIn>
            <div className="section-num">06 &mdash; THE OPPORTUNITY</div>
          </FadeIn>
          <WordReveal
            text="The window is open. Right now. Not next year."
            className="section-title"
            style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1.15, display: 'block', maxWidth: 550, marginBottom: 32 }}
          />

          <HexDivider />

          <StaggerContainer className="grid-3" staggerDelay={0.15}>
            {opportunities.map((opp, i) => (
              <StaggerItem key={i}>
                <motion.div
                  style={styles.oppCard}
                  whileHover={{ y: -4, borderColor: 'rgba(62, 117, 98, 0.2)' }}
                >
                  <RevealImage
                    src={opp.image}
                    alt={opp.title}
                    style={{ width: '100%', height: 160, marginBottom: 20 }}
                  />
                  <h4 style={styles.oppTitle}>{opp.title}</h4>
                  <p style={styles.oppDesc}>{opp.desc}</p>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Meet Our Avengers - Team */}
      <section className="section" style={{ background: '#0B0B0B' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 72 }}>
            <FadeIn>
              <div className="section-num" style={{ justifyContent: 'center' }}>08 &mdash; THE TEAM</div>
            </FadeIn>
            <WordReveal
              text="Meet Our Avengers"
              className="section-title"
              style={{ fontSize: 'clamp(2rem, 5vw, 3.2rem)', fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1.15, display: 'block' }}
            />
            <FadeIn delay={0.4}>
              <p className="section-subtitle" style={{ margin: '16px auto 0' }}>
                The minds behind HTV Digital
              </p>
            </FadeIn>
          </div>

          <StaggerContainer className="grid-3" staggerDelay={0.15}>
            {teamMembers.map((member, i) => (
              <StaggerItem key={i}>
                <motion.div
                  style={styles.teamCard}
                  whileHover={{ y: -8, borderColor: 'rgba(62, 117, 98, 0.2)' }}
                  transition={{ duration: 0.35 }}
                >
                  <div style={styles.teamImageWrap}>
                    <motion.img
                      src={member.photo}
                      alt={member.name}
                      style={styles.teamPhoto}
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                  <h3 style={styles.teamName}>{member.name}</h3>
                  <p style={styles.teamRole}>{member.role}</p>
                  <div style={{ width: 40, height: 1, background: 'rgba(62,117,98,0.2)', margin: '16px auto' }} />
                  <p style={styles.teamBio}>{member.bio}</p>
                  <div style={styles.teamSocials}>
                    <MagneticButton>
                      <span style={styles.teamSocialIcon}><FaLinkedinIn size={14} /></span>
                    </MagneticButton>
                    <MagneticButton>
                      <span style={styles.teamSocialIcon}><FaInstagram size={14} /></span>
                    </MagneticButton>
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>

          <FadeIn style={{ textAlign: 'center', marginTop: 40 }}>
            <p style={{ color: '#777', fontSize: '0.9rem' }}>
              Team of 10 specialists &middot; Hyderabad, Telangana
            </p>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

const htvValues = [
  { letter: 'H', name: 'Hive', desc: 'Collective intelligence — AI + expert collaboration working as one unified force.', color: '#3E7562' },
  { letter: 'T', name: 'Thrive', desc: 'Measurable growth that shows up in your revenue, not just a dashboard.', color: '#FFFFFF' },
  { letter: 'V', name: 'Ventive', desc: 'Bold innovation that outpaces competitors before they see you coming.', color: '#549E84' },
];

const missionCards = [
  { title: 'Our Mission', desc: 'To democratize premium digital services for Indian manufacturers using AI, making world-class branding accessible to every factory, every workshop, and every industrial brand.', image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&q=80' },
  { title: 'Our Vision', desc: 'A digital India where manufacturing excellence is matched by digital excellence — where no great product goes undiscovered because of a poor online presence.', image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&q=80' },
  { title: 'Our Approach', desc: 'We combine deep industry knowledge with cutting-edge AI tools. Every strategy is evidence-based, every creative is AI-enhanced, and every result is measurable.', image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=600&q=80' },
];

const opportunities = [
  { title: 'AI collapsed content costs by 80%', desc: 'What used to cost ₹5L in video production now costs ₹50K — with better output.', image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=600&q=80' },
  { title: 'Short-form video is #1 B2B discovery', desc: 'Procurement teams discover suppliers on Instagram & LinkedIn.', image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=600&q=80' },
  { title: 'First movers own the category', desc: 'Brands that go digital now will be impossible to displace by late movers.', image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80' },
];

const teamMembers = [
  { name: 'Hari', role: 'Co-Founder · Strategy', bio: 'Fortune 100 Consulting @ Deloitte. Corporate-grade strategy rigour for industrial brand growth.', photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80' },
  { name: 'Teja', role: 'Co-Founder · Industry Lead', bio: 'MD, Powertech Transformers. The insider who knows manufacturing from floor to boardroom.', photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&q=80' },
  { name: 'Venkat', role: 'Co-Founder · Creative', bio: '4th Gen Editor & Cinematographer, Telugu Film Industry. World-class visual storytelling.', photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&q=80' },
];

const styles = {
  heroSection: {
    minHeight: '70vh',
    display: 'flex',
    alignItems: 'center',
    paddingTop: 180,
    paddingBottom: 80,
    position: 'relative',
    overflow: 'hidden',
  },
  heroBg: {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
  },
  heroOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(11,11,11,0.88) 0%, rgba(11,11,11,0.75) 50%, rgba(11,11,11,0.95) 100%)',
  },
  htvCard: {
    padding: '80px 48px',
    textAlign: 'center',
    transition: '0.4s',
    background: 'transparent',
  },
  htvLetter: {
    fontSize: '4.5rem',
    fontWeight: 900,
    display: 'block',
    marginBottom: 8,
    letterSpacing: '-2px',
  },
  htvName: {
    fontSize: '1.3rem',
    fontWeight: 700,
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  htvDesc: {
    fontSize: '0.9rem',
    color: '#888',
    lineHeight: 1.8,
    maxWidth: 280,
    margin: '0 auto',
  },
  missionCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 40,
    padding: '48px 40px',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    transition: '0.4s',
  },
  missionLeft: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    flexShrink: 0,
  },
  missionNum: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#3E7562',
    letterSpacing: 2,
  },
  missionCenter: {
    flex: 1,
  },
  missionTitle: {
    fontSize: '1.5rem',
    fontWeight: 700,
    marginBottom: 12,
    letterSpacing: '-0.5px',
  },
  missionDesc: {
    fontSize: '0.95rem',
    color: '#888',
    lineHeight: 1.8,
    maxWidth: 500,
  },
  oppCard: {
    padding: 0,
    border: '1px solid rgba(255,255,255,0.04)',
    background: '#161618',
    transition: '0.4s',
    overflow: 'hidden',
  },
  oppTitle: {
    fontSize: '1.05rem',
    fontWeight: 600,
    marginBottom: 10,
    color: '#fff',
    padding: '0 24px',
  },
  oppDesc: {
    fontSize: '0.85rem',
    color: '#777',
    lineHeight: 1.7,
    padding: '0 24px 24px',
  },
  teamCard: {
    padding: 0,
    border: '1px solid rgba(255,255,255,0.04)',
    background: '#111112',
    textAlign: 'center',
    transition: '0.4s',
    overflow: 'hidden',
  },
  teamImageWrap: {
    width: '100%',
    height: 260,
    overflow: 'hidden',
    clipPath: 'polygon(0 0, 100% 0, 100% 85%, 50% 100%, 0 85%)',
  },
  teamPhoto: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  teamName: {
    fontSize: '1.15rem',
    fontWeight: 700,
    letterSpacing: '-0.3px',
    marginTop: 20,
    padding: '0 24px',
  },
  teamRole: {
    fontSize: '0.8rem',
    color: '#3E7562',
    fontWeight: 600,
    marginTop: 4,
    textTransform: 'uppercase',
    letterSpacing: 1,
    padding: '0 24px',
  },
  teamBio: {
    fontSize: '0.85rem',
    color: '#777',
    lineHeight: 1.7,
    padding: '0 24px',
  },
  teamSocials: {
    display: 'flex',
    justifyContent: 'center',
    gap: 10,
    marginTop: 16,
    paddingBottom: 28,
  },
  teamSocialIcon: {
    width: 32,
    height: 32,
    border: '1px solid rgba(62, 117, 98, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#777',
    fontSize: '0.8rem',
    cursor: 'pointer',
    transition: '0.3s',
  },
};
